import React from 'react';
import { Box } from '@mui/material';
import { Image } from 'antd';
import { sharedImagePreviewConfig } from 'utils/antImagePreviewEnhancements';

export type PhotoPreviewItem = {
  src: string;
  alt?: string;
  key?: string | number;
};

type PhotoPreviewThumbnailProps = {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
};

/** Single thumbnail with antd Image preview (zoom, rotate, close). */
export const PhotoPreviewThumbnail = ({
  src,
  alt = 'Photo',
  width = 80,
  height = 80,
  style
}: PhotoPreviewThumbnailProps) => (
  <Box
    sx={{
      width,
      height,
      border: '1px solid #ddd',
      borderRadius: 1,
      overflow: 'hidden',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      '& .ant-image, & .ant-image-img': {
        width: '100% !important',
        height: '100% !important',
        maxWidth: '100% !important',
        maxHeight: '100% !important',
        objectFit: 'cover'
      },
      ...style
    }}>
    <Image
      src={src}
      alt={alt}
      width="100%"
      height="100%"
      style={{
        objectFit: 'cover',
        width: '100%',
        height: '100%',
        display: 'block'
      }}
      preview={sharedImagePreviewConfig}
    />
  </Box>
);

type PhotoPreviewGroupProps = {
  photos: PhotoPreviewItem[];
  thumbnailWidth?: number;
  thumbnailHeight?: number;
};

/** Multiple thumbnails sharing one preview group (navigate between photos in preview). */
export const PhotoPreviewGroup = ({
  photos,
  thumbnailWidth = 80,
  thumbnailHeight = 80
}: PhotoPreviewGroupProps) => {
  if (!photos.length) return null;

  return (
    <Image.PreviewGroup>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {photos.map((photo, index) => (
          <PhotoPreviewThumbnail
            key={photo.key ?? index}
            src={photo.src}
            alt={photo.alt ?? `Photo ${index + 1}`}
            width={thumbnailWidth}
            height={thumbnailHeight}
          />
        ))}
      </Box>
    </Image.PreviewGroup>
  );
};
